import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, X, Send, Loader2 } from "lucide-react";
import { API } from "../lib/api";

const MODELS = [
  { key: "chatgpt", label: "ChatGPT" },
  { key: "claude", label: "Claude" },
  { key: "gemini", label: "Gemini" },
];

const GREETING = {
  role: "assistant",
  content: "Hi, I'm Ember — the RCSCW assistant. Ask me about the club, our projects, events or how to get involved.",
};

function getSessionId() {
  try {
    let id = localStorage.getItem("rcscw-chat-session");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("rcscw-chat-session", id);
    }
    return id;
  } catch (e) {
    return "anon";
  }
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState("chatgpt");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([GREETING]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = 99999;
  }, [messages, open]);

  const appendToLast = (text) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + text };
      return copy;
    });

  const setLast = (text) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content: text };
      return copy;
    });

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, model, session_id: sessionId.current }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const json = JSON.parse(payload);
            if (json.delta) appendToLast(json.delta);
            if (json.error) setLast(json.error);
          } catch (e) {}
        }
      }
    } catch (e) {
      setLast("Connection hiccup — please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[80] flex h-14 w-14 items-center justify-center rounded-full border border-ember/60 bg-navy-light text-ember shadow-[0_0_30px_rgba(226,232,240,0.28)] transition-transform hover:scale-105"
        aria-label="Chat with Ember"
        data-testid="chat-open-btn"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        {open ? <X size={22} /> : <Flame size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="card-line fixed bottom-24 right-4 z-[80] flex h-[560px] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden sm:right-6"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
          >
            <div className="border-b border-silver/15 bg-navy/80 p-4">
              <div className="flex items-center gap-3">
                <Flame size={17} className="text-ember" />
                <div className="flex-1">
                  <p className="font-display text-lg leading-none text-silver">Ember</p>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-silver-muted">The RCSCW assistant</p>
                </div>
              </div>
              <div className="mt-3 flex gap-1.5" data-testid="chat-model-switch">
                {MODELS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setModel(m.key)}
                    className={`border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-colors ${
                      model === m.key ? "border-ember bg-ember/10 text-ember" : "border-silver/20 text-silver-muted hover:text-silver"
                    }`}
                    data-testid={`chat-model-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-ember/90 text-ink"
                        : "border border-silver/15 bg-navy/70 text-silver-dim"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 border-t border-silver/15 bg-navy/80 p-3">
              <input
                className="input-field flex-1 !py-2.5"
                placeholder="Ask about the club…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="chat-input"
              />
              <button onClick={send} disabled={busy || !input.trim()} className="flex h-11 w-11 shrink-0 items-center justify-center bg-ember text-white transition-colors hover:bg-ember/80 disabled:opacity-50" aria-label="Send message" data-testid="chat-send-btn">
                <Send size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
